import React, { useEffect, useMemo, useState } from "react";
import {
  getQuizzVideos,
  deleteQuizzVideo,
} from "../../../services/quizzvideoServices";
import Button from "../../../components/Button/Button";
import useScrollToTop from "./../../../hooks/useScollToTop";
import { useNotification } from "../../../components/Notification/NotificationManager";
import QuizzArbitrageModal from "./QuizzArbitrageModal";

import {
  FaPlus,
  FaSearch,
  FaVideo,
  FaImage,
  FaGavel,
  FaFolderOpen,
  FaPen,
  FaTrash,
  FaChevronLeft,
  FaChevronRight,
  FaSyncAlt,
  FaExternalLinkAlt,
} from "react-icons/fa";

/* ================= Helpers ================= */
const PAGE_SIZE = 18;

const normalize = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const short = (s = "", n = 140) => {
  const t = String(s || "").trim();
  if (t.length <= n) return t;
  return `${t.slice(0, n)}…`;
};

const normalizeImageUrl = (url) => {
  if (!url) return null;
  const trimmed = String(url).trim();
  if (!trimmed) return null;

  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith("//")) {
    return trimmed;
  }

  if (trimmed.startsWith("/")) {
    return `${window.location.origin}${trimmed}`;
  }

  return `${window.location.origin}/${trimmed}`;
};

const useDebounced = (value, delay = 300) => {
  const [v, setV] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return v;
};

const applyFiltersAndSort = (quizzList, term, famille) => {
  let list = Array.isArray(quizzList) ? [...quizzList] : [];
  const q = normalize(term);

  if (q) {
    list = list.filter((item) =>
      normalize(
        [
          item.nom_quizz,
          item.famille_quizz,
          item.decision,
          item.description,
          item.lien_url_video,
        ]
          .filter(Boolean)
          .join(" ")
      ).includes(q)
    );
  }

  if (famille !== "all") {
    list = list.filter(
      (item) => normalize(item.famille_quizz) === normalize(famille)
    );
  }

  list.sort((a, b) => {
    const famA = normalize(a.famille_quizz);
    const famB = normalize(b.famille_quizz);
    if (famA !== famB) return famA.localeCompare(famB);

    const nameA = normalize(a.nom_quizz);
    const nameB = normalize(b.nom_quizz);
    if (nameA !== nameB) return nameA.localeCompare(nameB);

    return Number(a.id ?? 0) - Number(b.id ?? 0);
  });

  return list;
};

/* ================= UI atoms ================= */
function CompactStat({ icon, label, value, accent = "indigo" }) {
  const accents = {
    indigo:
      "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    emerald:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
    amber:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
    rose:
      "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300",
  };

  return (
    <div className={`rounded-2xl border px-3 py-3 ${accents[accent]}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/80 dark:bg-slate-900/50">
          {icon}
        </div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.16em] opacity-75">
            {label}
          </div>
          <div className="mt-0.5 text-xl font-bold text-slate-900 dark:text-white">
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminPageHeader({
  totalQuizz,
  famillesCount,
  withVideosCount,
  withImagesCount,
}) {
  return (
    <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Administration
            </p>
            <h1 className="mt-1 text-2xl font-bold text-slate-900 sm:text-[30px] dark:text-white">
              Vidéos quizz arbitrage
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-600 dark:text-slate-300">
              Gérez les vidéos quizz d’arbitrage, leurs familles, la décision
              attendue et les ressources visuelles associées.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4 xl:min-w-[820px]">
            <CompactStat
              icon={<FaGavel />}
              label="Quizz"
              value={totalQuizz}
              accent="indigo"
            />
            <CompactStat
              icon={<FaFolderOpen />}
              label="Familles"
              value={famillesCount}
              accent="emerald"
            />
            <CompactStat
              icon={<FaVideo />}
              label="Vidéos"
              value={withVideosCount}
              accent="amber"
            />
            <CompactStat
              icon={<FaImage />}
              label="Visuels"
              value={withImagesCount}
              accent="rose"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function FiltersBar({
  searchTerm,
  setSearchTerm,
  familleFilter,
  setFamilleFilter,
  familyOptions,
  onReset,
  onRefresh,
  onCreate,
  loading,
}) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr),260px,auto]">
        <div className="relative">
          <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Rechercher un quizz par nom, famille, décision ou description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-11 w-full rounded-2xl border border-slate-300 bg-white pl-10 pr-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
          />
        </div>

        <select
          value={familleFilter}
          onChange={(e) => setFamilleFilter(e.target.value)}
          className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
        >
          <option value="all">Toutes les familles</option>
          {familyOptions.map((fam) => (
            <option key={fam} value={fam}>
              {fam}
            </option>
          ))}
        </select>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={onReset}
            disabled={loading}
            className="inline-flex h-11 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Réinitialiser
          </button>

          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="inline-flex h-11 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaSyncAlt />
            Rafraîchir
          </button>

          <Button
            text="Ajouter un quizz"
            onClick={onCreate}
            icon={FaPlus}
            className="!h-11 !rounded-2xl !px-4"
          />
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
        <FaVideo className="text-2xl" />
      </div>

      <h3 className="mt-4 text-xl font-bold text-slate-900 dark:text-white">
        Aucun quizz trouvé
      </h3>

      <p className="mx-auto mt-2 max-w-xl text-sm text-slate-600 dark:text-slate-300">
        Aucun quizz ne correspond à vos filtres actuels. Vous pouvez modifier la
        recherche, changer la famille affichée ou créer un nouveau quizz.
      </p>

      <div className="mt-5">
        <Button
          text="Créer un quizz"
          onClick={onCreate}
          icon={FaPlus}
          className="!rounded-2xl !px-5 !py-2.5"
        />
      </div>
    </div>
  );
}

function PreviewImage({ src, alt }) {
  const normalized = normalizeImageUrl(src);
  const [imgSrc, setImgSrc] = useState(normalized);

  useEffect(() => {
    setImgSrc(normalized);
  }, [normalized]);

  if (!normalized || !imgSrc) {
    return (
      <div className="flex h-20 w-32 items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-500">
        <FaImage className="text-xl" />
      </div>
    );
  }

  return (
    <img
      src={imgSrc}
      alt={alt}
      onError={() => setImgSrc(null)}
      className="h-20 w-32 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
    />
  );
}

function DecisionBadge({ decision }) {
  return (
    <span className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300">
      {decision || "Décision non renseignée"}
    </span>
  );
}

function DesktopTable({
  rows,
  loading,
  onEdit,
  onDelete,
  onCreate,
}) {
  return (
    <div className="hidden lg:block rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      {loading ? (
        <div className="px-4 py-10 text-center text-slate-500">Chargement…</div>
      ) : rows.length === 0 ? (
        <div className="p-6">
          <EmptyState onCreate={onCreate} />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-3xl">
          <table className="w-full min-w-[1260px] table-fixed text-sm">
            <thead className="bg-slate-50 text-left dark:bg-slate-950/60">
              <tr className="text-slate-600 dark:text-slate-300">
                <th className="w-[26%] px-6 py-3 font-semibold">Quizz</th>
                <th className="w-[14%] px-6 py-3 font-semibold">Famille</th>
                <th className="w-[14%] px-6 py-3 font-semibold">Aperçu</th>
                <th className="w-[18%] px-6 py-3 font-semibold">Décision</th>
                <th className="w-[16%] px-6 py-3 font-semibold">Vidéo</th>
                <th className="w-[12%] px-6 py-3 font-semibold">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {rows.map((quizz) => (
                <tr
                  key={quizz.id}
                  className="align-top transition hover:bg-slate-50/80 dark:hover:bg-slate-800/40"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-start gap-3">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-violet-600 text-white shadow-sm">
                        <FaVideo />
                      </div>

                      <div className="min-w-0">
                        <div className="text-[15px] font-semibold leading-5 text-slate-900 dark:text-white">
                          {quizz.nom_quizz || "Sans nom"}
                        </div>

                        <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                          ID #{quizz.id}
                        </div>

                        {quizz.description ? (
                          <div className="mt-2 line-clamp-3 text-[12px] text-slate-600 dark:text-slate-300">
                            {short(quizz.description, 170)}
                          </div>
                        ) : (
                          <div className="mt-2 text-[12px] italic text-slate-400">
                            Aucune description.
                          </div>
                        )}
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
                      {quizz.famille_quizz || "Sans famille"}
                    </span>
                  </td>

                  <td className="px-6 py-4">
                    <PreviewImage src={quizz.lien_url_image} alt={quizz.nom_quizz} />
                  </td>

                  <td className="px-6 py-4">
                    <DecisionBadge decision={quizz.decision} />
                  </td>

                  <td className="px-6 py-4">
                    {quizz.lien_url_video ? (
                      <a
                        href={quizz.lien_url_video}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
                      >
                        <FaExternalLinkAlt />
                        Ouvrir la vidéo
                      </a>
                    ) : (
                      <span className="text-xs text-slate-400">Aucune vidéo</span>
                    )}
                  </td>

                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        title="Éditer"
                        onClick={() => onEdit(quizz)}
                        className="inline-flex h-10 items-center gap-2 rounded-2xl bg-indigo-600 px-4 text-sm font-semibold text-white transition hover:bg-indigo-700"
                      >
                        <FaPen />
                      </button>

                      <button
                        type="button"
                        title="Supprimer"
                        onClick={() => onDelete(quizz)}
                        className="inline-flex h-10 items-center gap-2 rounded-2xl bg-red-600 px-4 text-sm font-semibold text-white transition hover:bg-red-700"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MobileCards({
  rows,
  loading,
  onEdit,
  onDelete,
  onCreate,
}) {
  return (
    <div className="space-y-3 lg:hidden">
      {loading ? (
        <div className="rounded-3xl border border-slate-200 bg-white px-4 py-10 text-center text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          Chargement…
        </div>
      ) : rows.length === 0 ? (
        <EmptyState onCreate={onCreate} />
      ) : (
        rows.map((quizz) => (
          <div
            key={quizz.id}
            className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900"
          >
            <div className="flex items-start gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-violet-600 text-white shadow-sm">
                <FaVideo />
              </div>

              <div className="min-w-0 flex-1">
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  {quizz.nom_quizz || "Sans nom"}
                </h3>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  ID #{quizz.id}
                </p>

                <div className="mt-2">
                  <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
                    {quizz.famille_quizz || "Sans famille"}
                  </span>
                </div>

                {quizz.description ? (
                  <p className="mt-3 line-clamp-3 text-sm text-slate-600 dark:text-slate-300">
                    {short(quizz.description, 180)}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="mt-4">
              <PreviewImage src={quizz.lien_url_image} alt={quizz.nom_quizz} />
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                  Décision attendue
                </div>
                <div className="mt-2">
                  <DecisionBadge decision={quizz.decision} />
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                  Vidéo
                </div>

                <div className="mt-2">
                  {quizz.lien_url_video ? (
                    <a
                      href={quizz.lien_url_video}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      <FaExternalLinkAlt />
                      Ouvrir la vidéo
                    </a>
                  ) : (
                    <span className="text-xs text-slate-400">Aucune vidéo</span>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                type="button"
                title="Éditer"
                onClick={() => onEdit(quizz)}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-3 text-sm font-semibold text-white transition hover:bg-indigo-700"
              >
                <FaPen />
              </button>

              <button
                type="button"
                title="Supprimer"
                onClick={() => onDelete(quizz)}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl bg-red-600 px-3 text-sm font-semibold text-white transition hover:bg-red-700"
              >
                <FaTrash />
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function PaginationBar({ page, pageCount, setPage, loading }) {
  if (pageCount <= 1) return null;

  return (
    <div className="mt-5 flex flex-col items-center justify-between gap-3 rounded-3xl border border-slate-200 bg-white px-4 py-4 sm:flex-row dark:border-slate-800 dark:bg-slate-900">
      <div className="text-sm text-slate-500 dark:text-slate-400">
        Page{" "}
        <span className="font-semibold text-slate-900 dark:text-white">
          {page}
        </span>{" "}
        sur{" "}
        <span className="font-semibold text-slate-900 dark:text-white">
          {pageCount}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1 || loading}
          className="inline-flex h-10 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <FaChevronLeft />
          Précédent
        </button>

        <button
          type="button"
          onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
          disabled={page === pageCount || loading}
          className="inline-flex h-10 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          Suivant
          <FaChevronRight />
        </button>
      </div>
    </div>
  );
}

/* ================= Page ================= */
const QuizzArbitrage = () => {
  useScrollToTop();
  const { addNotification } = useNotification();

  const [allQuizz, setAllQuizz] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedQuizz, setSelectedQuizz] = useState(null);
  const [modalMode, setModalMode] = useState("create");

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounced(searchTerm, 300);
  const [familleFilter, setFamilleFilter] = useState("all");

  const [page, setPage] = useState(1);

  const fetchQuizz = async () => {
    try {
      setLoading(true);
      const data = await getQuizzVideos();
      setAllQuizz(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des quizz vidéo", error);
      addNotification("Erreur lors de la récupération des quizz vidéo.", "error");
      setAllQuizz([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuizz();
  }, []);

  const familyOptions = useMemo(() => {
    return [...new Set(allQuizz.map((q) => q.famille_quizz).filter(Boolean))].sort(
      (a, b) => a.localeCompare(b)
    );
  }, [allQuizz]);

  const filteredQuizz = useMemo(
    () => applyFiltersAndSort(allQuizz, debouncedSearch, familleFilter),
    [allQuizz, debouncedSearch, familleFilter]
  );

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, familleFilter]);

  const totalQuizz = allQuizz.length;
  const famillesCount = familyOptions.length;
  const withVideosCount = allQuizz.filter((q) => !!q.lien_url_video).length;
  const withImagesCount = allQuizz.filter((q) => !!q.lien_url_image).length;

  const pageCount = Math.max(1, Math.ceil(filteredQuizz.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);

  const paginatedQuizz = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredQuizz.slice(start, start + PAGE_SIZE);
  }, [filteredQuizz, safePage]);

  const openCreateModal = () => {
    setSelectedQuizz(null);
    setModalMode("create");
    setIsModalOpen(true);
  };

  const openEditModal = (quizz) => {
    setSelectedQuizz(quizz);
    setModalMode("edit");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedQuizz(null);
    setIsModalOpen(false);
  };

  const handleDeleteQuizz = async (quizz) => {
    if (
      !window.confirm(
        `Êtes-vous sûr de vouloir supprimer le quizz "${quizz.nom_quizz}" ?`
      )
    ) {
      return;
    }

    try {
      const result = await deleteQuizzVideo(quizz.id);

      if (!result && result !== 0) {
        throw new Error("Suppression impossible");
      }

      addNotification("Quizz supprimé avec succès.", "success");
      setAllQuizz((prev) => prev.filter((q) => q.id !== quizz.id));
    } catch (error) {
      console.error("Erreur lors de la suppression d’un quizz vidéo", error);
      addNotification("Erreur lors de la suppression du quizz vidéo.", "error");
    }
  };

  const handleReset = () => {
    setSearchTerm("");
    setFamilleFilter("all");
    setPage(1);
  };

  return (
    <div className="w-full pb-10 pt-[96px] font-satoshi">
      <div className="mx-auto max-w-[1400px] px-4">
        <AdminPageHeader
          totalQuizz={totalQuizz}
          famillesCount={famillesCount}
          withVideosCount={withVideosCount}
          withImagesCount={withImagesCount}
        />

        <div className="mt-5">
          <FiltersBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            familleFilter={familleFilter}
            setFamilleFilter={setFamilleFilter}
            familyOptions={familyOptions}
            onReset={handleReset}
            onRefresh={fetchQuizz}
            onCreate={openCreateModal}
            loading={loading}
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <span className="rounded-full bg-slate-100 px-3 py-1 dark:bg-slate-800">
            {filteredQuizz.length} quizz
          </span>

          {familleFilter !== "all" && (
            <span className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
              Famille : {familleFilter}
            </span>
          )}

          {debouncedSearch && (
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              Recherche : {searchTerm}
            </span>
          )}

          {!loading && filteredQuizz.length > 0 && (
            <span className="ml-auto text-xs">
              Affichage {paginatedQuizz.length} • page {safePage} / {pageCount}
            </span>
          )}
        </div>

        <div className="mt-5">
          <DesktopTable
            rows={paginatedQuizz}
            loading={loading}
            onEdit={openEditModal}
            onDelete={handleDeleteQuizz}
            onCreate={openCreateModal}
          />

          <MobileCards
            rows={paginatedQuizz}
            loading={loading}
            onEdit={openEditModal}
            onDelete={handleDeleteQuizz}
            onCreate={openCreateModal}
          />
        </div>

        <PaginationBar
          page={safePage}
          pageCount={pageCount}
          setPage={setPage}
          loading={loading}
        />
      </div>

      {isModalOpen && (
        <QuizzArbitrageModal
          isActive={isModalOpen}
          onClose={closeModal}
          refreshQuizz={fetchQuizz}
          mode={modalMode}
          quizzData={selectedQuizz}
        />
      )}
    </div>
  );
};

export default QuizzArbitrage;